import { Component, Injector, OnInit } from '@angular/core';
import { GlobalConstants } from 'src/app/core/constants/global-constants.constant';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';
import { UserPreferenceConstants } from 'src/app/core/constants/user-preference-constants.constant';
import { CommonDataService } from 'src/app/core/services/common-data.service';
import { DropdownOptionsService } from 'src/app/core/services/dropdown-options.service';
import { ExpenseService } from 'src/app/core/services/expense.service';
import { isNullOrEmpty } from 'src/app/core/utils/common-functions';
import { SessionBaseComponent } from 'src/app/shared/components/session.base.component';

interface dropDownList {
  label?: string | null;
  value?: string | null;
}
interface table {
  field: string;
  header: string;
  sort: Boolean;
}
interface viewResult {
  seqNo: string | null;
  facility: string | null;
  deliveryMode: string | null;
  forwarder: string | null;
  freight: number | null;
  fuelCosts: number | null;
  securityCheckFee: number | null;
  otherFee: number | null;
  estimateTotalCharge: number | null;
  currency: string | null;
  incoTerm: string | null;
}

interface previewCalculationBeanDetail {
  rfqFeeType: string | null;
  estimatedAmount: string;
}

interface previewCalculationBean {
  headerEntity: {
    facility: string;
    forwarderCode: string;
    currency: string;
    estimateTotalCharge: number | null;
    shippingType: string;
  };
  detailEntities: previewCalculationBeanDetail[];
}

interface facilityOptionsByOptionType {
  optionDesc: string | null;
  optionCode: string | null;
}

interface codeConverter {
  codeKey: string | null;
  codeDesc: string | null;
}

interface vCountryOptions {
  countryName: string | null;
  countryCode: string | null;
}

interface vCityOptions {
  name?: string;
  seqNo: number;
  expressArea?: string;
  country?: string;
}

@Component({
  selector: 'laas-freight-trial-calculation',
  templateUrl: './freight-trial-calculation.component.html',
  styleUrls: ['./freight-trial-calculation.component.scss'],
  providers: [
    {
      provide: SessionBaseComponent,
      useClass: FreightTrialCalculationComponent,
    },
  ],
})
export class FreightTrialCalculationComponent
  extends SessionBaseComponent
  implements OnInit
{
  readonly viewPermissionCode = PermissionConstants.FreightTrialCalculation;
  readonly preferenceKey: string;
  facilityOptions: any[] = [];
  shippingTypeOptions: dropDownList[] = [];
  incoTermList: dropDownList[] = [];
  countryList: dropDownList[] = [];
  allCityList: vCityOptions[] = [];
  cityListFrom: dropDownList[] = [];
  cityListTo: dropDownList[] = [];
  weightList: dropDownList[] = [];
  freightTrialCalculationResult: viewResult[] = [];
  tableColumns: table[];
  selectedRow: any[] = [];
  searchParams: any;
  currentLang: string = this.laasSetting.language;
  isLoading: boolean = false;

  vaildFromCountry: boolean = false;
  vaildToCountry: boolean = false;
  // bookingForwarderList: any = [];

  constructor(
    injector: Injector,
    private dropdownOptionsService: DropdownOptionsService,
    private commonDataService: CommonDataService,
    private dropdownOptionService: DropdownOptionsService,
    private expenseService: ExpenseService
  ) {
    super(injector);
    this.preferenceKey = UserPreferenceConstants.FreightTrialCalculation;
    this.searchParams = {
      facility: null,
      shippingType: null,
      incoTerm: null,
      fromCountry: null,
      fromCity: null,
      toCountry: null,
      toCity: null,
      grossLong: 0.0,
      grossWidth: 0.0,
      grossHigh: 0.0,
      totalGrossWeight: null,
      totalVolumeWeight: null,
      deliverySubType: 1,
    };

    this.tableColumns = [
      { field: 'facility', header: 'FreightField.Facility', sort: true },
      {
        field: 'deliveryMode',
        header: 'FreightField.DeliveryMode',
        sort: true,
      },
      {
        field: 'fromCountry',
        header: 'FreightField.Other.OriginCountry',
        sort: true,
      },
      {
        field: 'fromCity',
        header: 'FreightField.Other.OriginCity',
        sort: true,
      },
      {
        field: 'toCountry',
        header: 'FreightField.Other.DestinationCountry',
        sort: true,
      },
      {
        field: 'toCity',
        header: 'FreightField.Other.DestinationCity',
        sort: true,
      },
      { field: 'forwarder', header: 'FreightField.Forwarder', sort: true },
      { field: 'freight', header: 'FreightField.Freight', sort: true },
      { field: 'fuelCosts', header: 'FreightField.FuelCosts', sort: true },
      { field: 'calWeight', header: 'FreightField.CalWeight', sort: true },
      {
        field: 'securityCheckFee',
        header: 'FreightField.SecurityCheckFee',
        sort: true,
      },
      { field: 'otherFee', header: 'FreightField.OtherFee', sort: true },
      {
        field: 'estimateTotalCharge',
        header: 'FreightField.EstimateTotalCharge',
        sort: true,
      },
      { field: 'currency', header: 'FreightField.Currency', sort: true },
      { field: 'incoTerm', header: 'FreightField.IncoTerm', sort: true },
      { field: 'isRfq', header: 'RFQ', sort: true },
      { field: 'consigneeKey', header: 'FreightField.Consignee', sort: true },
    ];
  }

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();
    this.dropdownOptionsService
      .findFacilityOptionsByOptionType('ShippingType')
      .subscribe({
        next: (options: facilityOptionsByOptionType[]) => {
          this.shippingTypeOptions = options.map((x) => ({
            label: x.optionDesc,
            value: x.optionCode,
          }));
        },
      });

    //incoTerm Options
    this.commonDataService
      .getCodeConverter(
        this.laasSetting.language,
        'DropdownList',
        'WarehousingShipTerm'
      )
      .subscribe({
        next: (result) => {
          this.incoTermList = [...result].map((x: codeConverter) => {
            return {
              label: `${x.codeKey}-${x.codeDesc}`,
              value: x.codeKey,
            };
          });

          this.incoTermList = this.incoTermList.filter(
            (i: any) => i.value != 'ALL'
          );
        },
      });

    this.dropdownOptionsService.getVCountryOptions().subscribe({
      next: (options: vCountryOptions[]) => {
        this.countryList = options.map((x) => ({
          label: x.countryName,
          value: x.countryCode,
        }));
      },
    });

    this.dropdownOptionsService.getVCityOptions().subscribe({
      next: (options: vCityOptions[]) => {
        this.allCityList = options.map((x) => ({
          name: x.name,
          seqNo: x.seqNo,
          expressArea: x.expressArea,
          country: x.country,
        }));

        if (this.routeStateService.getCurrent()) {
          const forwarderListFormValue =
            this.routeStateService.getCurrent().data.serchParams;
          this.searchParams.fromCity = forwarderListFormValue.fromCity;
          this.searchParams.fromCountry = forwarderListFormValue.fromCountry;
          this.searchParams.toCity = forwarderListFormValue.toCity;
          this.searchParams.toCountry = forwarderListFormValue.toCountry;
          this.searchParams.shippingType = forwarderListFormValue.shippingType;
          this.searchParams.incoTerm = forwarderListFormValue.shipTerm;

          if (!isNullOrEmpty(this.searchParams.fromCountry)) {
            this.cityListFrom = this.allCityList
              .filter((x) => x.country == this.searchParams.fromCountry)
              .map((x) => ({ label: x.name, value: x.seqNo.toString() }));
          }
          if (!isNullOrEmpty(this.searchParams.toCountry)) {
            this.cityListTo = this.allCityList
              .filter((x) => x.country == this.searchParams.toCountry)
              .map((x) => ({ label: x.name, value: x.seqNo.toString() }));
          }
        }
      },
    });

    this.weightList = [
      { label: '材積', value: 'Gross' },
      { label: '重量', value: 'Volume' },
    ];

    this.dropdownOptionsService.getFacilityOptions().subscribe({
      next: (options: any[]) => {
        this.facilityOptions = options.map((x) => ({
          label: x.optionDesc,
          value: x.optionCode,
        }));
      },
    });
  }

  showValidationMessage(value: any): boolean {
    let show: boolean = isNullOrEmpty(value);
    return show || (typeof value === 'number' && value <= 0);
  }

  showCountryValidationMessage(country: any, city: any): boolean {
    return isNullOrEmpty(country) || isNullOrEmpty(city);
  }

  async getForwarderOptions(facilityCode?: string) {
    return await this.dropdownOptionsService
      .getForwarderOptions(facilityCode, true)
      .toPromise();
  }

  searchClickBtn(event: any): void {
    console.log('Current viewport width:', window.innerWidth);
    let isDefaultLanguage: boolean =
      this.laasSetting.language == GlobalConstants.defaultLanguage;
    let errorMessage: string = '';
    if (this.showValidationMessage(this.searchParams.incoTerm)) {
      errorMessage = isDefaultLanguage
        ? '貿易條件 必填'
        : 'Ship Term is a required field.';
      this.toastErrorMessage(errorMessage);
      return;
    }
    if (
      this.showValidationMessage(this.searchParams.fromCountry) ||
      this.showValidationMessage(this.searchParams.fromCity) ||
      this.showValidationMessage(this.searchParams.toCountry) ||
      this.showValidationMessage(this.searchParams.toCity)
    ) {
      errorMessage = isDefaultLanguage
        ? '來源地 / 目的地 必填'
        : 'Origin/Destination Country is a required field.';
      this.toastErrorMessage(errorMessage);
      return;
    }
    if (
      this.showValidationMessage(this.searchParams.totalGrossWeight) &&
      this.showValidationMessage(this.searchParams.totalVolumeWeight)
    ) {
      errorMessage = isDefaultLanguage
        ? '總重 / 總材積重 兩者需擇一必填且不可小於等於 0'
        : 'One of the Total Gross Weight and Total Volume Weight must be selected and must not be less than or equal to 0.';
      this.toastErrorMessage(errorMessage);
      return;
    }
    if (this.vaildFromCountry || this.vaildToCountry) {
      return;
    }

    this.isLoading = true;

    this.expenseService.calculationOfAllForwarder(this.searchParams).subscribe({
      next: (result) => {
        let seqCounter = 1;
        this.freightTrialCalculationResult = Object.keys(result).reduce(
          (finalList, key) => {
            const calWeight = parseFloat(key);
            const previewCalculationBeans = result[key];
            const mappedBeans = previewCalculationBeans
              .map((previewCalculationBean: any) => {
                const freight = this.roundToInteger(
                  previewCalculationBean.detailEntities
                    .filter(
                      (d: previewCalculationBeanDetail) => d.rfqFeeType == '1'
                    )
                    .map((d: previewCalculationBeanDetail) =>
                      parseFloat(d.estimatedAmount)
                    )
                    .reduce((a: number, b: number) => a + b, 0)
                );
                const fuelCosts = this.roundToInteger(
                  previewCalculationBean.detailEntities
                    .filter(
                      (d: previewCalculationBeanDetail) => d.rfqFeeType == '101'
                    )
                    .map((d: previewCalculationBeanDetail) =>
                      parseFloat(d.estimatedAmount)
                    )
                    .reduce((a: number, b: number) => a + b, 0)
                );
                const securityCheckFee = this.roundToInteger(
                  previewCalculationBean.detailEntities
                    .filter(
                      (d: previewCalculationBeanDetail) => d.rfqFeeType == '102'
                    )
                    .map((d: previewCalculationBeanDetail) =>
                      parseFloat(d.estimatedAmount)
                    )
                    .reduce((a: number, b: number) => a + b, 0)
                );
                const otherFee = this.roundToInteger(
                  previewCalculationBean.detailEntities
                    .filter(
                      (d: previewCalculationBeanDetail) =>
                        d.rfqFeeType !== '1' &&
                        d.rfqFeeType !== '101' &&
                        d.rfqFeeType !== '102'
                    )
                    .map((d: previewCalculationBeanDetail) =>
                      parseFloat(d.estimatedAmount)
                    )
                    .reduce((a: number, b: number) => a + b, 0)
                );

                const estimateTotalCharge = this.roundToInteger(
                  freight + fuelCosts + securityCheckFee + otherFee
                );

                return {
                  seqNo: seqCounter++,
                  facility: previewCalculationBean.headerEntity.facility,
                  deliveryMode:
                    previewCalculationBean.headerEntity.shippingType,
                  forwarder: previewCalculationBean.headerEntity.forwarderCode,
                  currency: previewCalculationBean.headerEntity.currency,
                  incoTerm: this.searchParams.incoTerm,
                  isRfq: previewCalculationBean.headerEntity.isRfq,
                  consigneeKey:
                    previewCalculationBean.headerEntity.consigneeKey,
                  fromCity: previewCalculationBean.headerEntity.fromCity,
                  fromCountry: previewCalculationBean.headerEntity.fromCountry,
                  toCountry: previewCalculationBean.headerEntity.toCountry,
                  toCity: previewCalculationBean.headerEntity.toCity,
                  calWeight,
                  freight,
                  fuelCosts,
                  securityCheckFee,
                  otherFee,
                  estimateTotalCharge,
                };
              });

            return finalList.concat(mappedBeans);
          },
          []
        );

        this.freightTrialCalculationResult.forEach((x: any) => {
          let forwarder = x.forwarder;
          let facility = this.getFirstFacility(x.facility);
          this.getForwarderOptions(facility).then((result) => {
            let forwarderOption = result.filter(
              (y: any) => y.optionCode == forwarder
            );
            if (forwarderOption.length > 0) {
              x.forwarder = forwarderOption[0].optionDesc;
            }
          });
        });
      },
      error: (err: any) => {
        this.toastErrorMessage(err.error);
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
        console.log(this.freightTrialCalculationResult);
      },
    });
  }

  getFirstFacility(facilitys: string): string | undefined {
    if (isNullOrEmpty(facilitys)) {
      return undefined;
    }

    let index = facilitys.indexOf(',');
    if (index < 0) {
      return facilitys;
    }

    return facilitys.substring(0, index);
  }

  getFromCityOptions(country: string): void {
    if (country == 'from') {
      this.cityListFrom = this.allCityList
        .filter((x) => x.country == this.searchParams.fromCountry)
        .map((x) => ({ label: x.name, value: x.seqNo.toString() }));
      this.vaildFromCountry = this.showCountryValidationMessage(
        this.searchParams.fromCountry,
        this.searchParams.fromCity
      );
    } else if (country == 'to') {
      this.cityListTo = this.allCityList
        .filter((x) => x.country == this.searchParams.toCountry)
        .map((x) => ({ label: x.name, value: x.seqNo.toString() }));
      this.vaildToCountry = this.showCountryValidationMessage(
        this.searchParams.toCountry,
        this.searchParams.toCity
      );
    }
  }

  getFromCity(country: string): void {
    if (country == 'from') {
      this.vaildFromCountry = this.showCountryValidationMessage(
        this.searchParams.fromCountry,
        this.searchParams.fromCity
      );
    } else if (country == 'to') {
      this.vaildToCountry = this.showCountryValidationMessage(
        this.searchParams.toCountry,
        this.searchParams.toCity
      );
    }
  }

  private roundToInteger(num: number): number {
    return Math.round(num);
  }

  resetClickBtn(event: any): void {
    this.freightTrialCalculationResult = [];

    this.searchParams = {
      facility: null,
      shippingType: null,
      incoTerm: null,
      fromCountry: null,
      fromCity: null,
      toCountry: null,
      toCity: null,
      grossLong: 0.0,
      grossWidth: 0.0,
      grossHigh: 0.0,
      totalGrossWeight: null,
      totalVolumeWeight: null,
      deliverySubType: 1,
    };
  }

  exportResult() {
    if (this.selectedRow.length == 0 || this.selectedRow == null) {
      this.toastService.warn('Please select rows.');
      return;
    }
    this.selectedRow = this.selectedRow.map((row) => ({
      ...row, // 保留原有屬性
      fromCountry: this.searchParams.fromCountry || null,
      fromCity: this.searchParams.fromCity || null,
      toCountry: this.searchParams.toCountry || null,
      toCity: this.searchParams.toCity || null,
    }));
    this.expenseService.exportFreightTrail(this.selectedRow).subscribe({
      next: (result) => {
        if (result == null || result.size == 0) {
          this.translateService.get('Common.Toast.NoData').subscribe({
            next: (i18nResult) => {
              this.toastService.info(i18nResult);
            },
          });
          this.isLoading = false;
        } else {
          const file = result;

          file.name =
            'FreightTrial_' + new Date().toLocaleDateString() + '.xlsx';

          const url = URL.createObjectURL(file);
          let a: any = document.createElement('a');
          a.href = url;
          a.download = file.name;
          a.click();
          URL.revokeObjectURL(url);
        }
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }
}
